import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Rocket, Edit, Trash2, XCircle, CheckCheck, Eye, MessageSquare, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { api, apiErrorMessage } from '../apiClient';
import { toast } from 'sonner';

const STATUS_STYLE = {
  draft: { bg: 'bg-amber-50', fg: 'text-amber-600' },
  submitted: { bg: 'bg-pink-50', fg: 'text-pink-700' },
  live: { bg: 'bg-emerald-50', fg: 'text-emerald-600' },
  completed: { bg: 'bg-pink-50', fg: 'text-pink-700' },
  cancelled: { bg: 'bg-red-50', fg: 'text-red-500' },
};

export default function OrgEventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/organizer/events/${id}`);
      setEvent(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setEvent(null);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const doAction = async (path) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/organizer/events/${id}/${path}`);
      toast.success(path === 'submit' ? 'Event submitted for review' : path === 'cancel' ? 'Event cancelled' : 'Event completed');
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete draft? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.delete(`/organizer/events/${id}`);
      toast.success('Draft deleted');
      navigate('/organizer/events');
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const confirmCancel = () => {
    if (window.confirm('Cancel this event? This cannot be undone.')) doAction('cancel');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>;
  if (!event) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <p className="text-gray-900 font-bold">Event not found</p>
      <button onClick={() => navigate(-1)} className="text-brand-magenta font-bold text-sm">Back</button>
    </div>
  );

  const st = STATUS_STYLE[event.status || 'live'] || STATUS_STYLE.live;
  const endPassed = dayjs(event.end_datetime).isBefore(dayjs());

  return (
    <div className="max-w-4xl mx-auto" data-testid="org-event-detail-page">
      {/* Banner */}
      <div className="relative">
        <img src={event.banner_url} alt="" className="w-full h-[260px] object-cover bg-gray-100" />
        <div className="absolute top-0 left-0 right-0 flex justify-between items-start p-4 pt-6">
          <button data-testid="oe-back-btn" onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center">
            <ChevronLeft className="w-5 h-5 text-gray-900" />
          </button>
          <span className={`${st.bg} ${st.fg} text-[11px] font-black tracking-wide px-3 py-1.5 rounded-full`}>
            {(event.status || 'LIVE').toUpperCase()}
          </span>
        </div>
      </div>

      <div className="-mt-5 relative bg-gray-50 rounded-t-3xl px-6 pt-6 pb-8">
        <h1 className="text-2xl font-black text-gray-900 tracking-tight" data-testid="oe-title">{event.title}</h1>
        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">{event.category} · {event.city}</p>

        {/* Stats */}
        <div className="flex mt-4 bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex-1 text-center">
            <p className="text-lg font-black text-gray-900 flex items-center justify-center gap-1"><Eye className="w-4 h-4 text-gray-400" />{event.view_count}</p>
            <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">Views</p>
          </div>
          <div className="w-px bg-gray-100" />
          <div className="flex-1 text-center">
            <p className="text-lg font-black text-gray-900 flex items-center justify-center gap-1"><MessageSquare className="w-4 h-4 text-gray-400" />{event.enquiry_count}</p>
            <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">Enquiries</p>
          </div>
          <div className="w-px bg-gray-100" />
          <div className="flex-1 text-center">
            <p className="text-lg font-black text-gray-900">{event.age_restriction}</p>
            <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">Age</p>
          </div>
        </div>

        {/* When */}
        <div className="mt-6">
          <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-wider mb-1.5">When</h3>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-[15px] font-extrabold text-gray-900">{dayjs(event.start_datetime).format('dddd, MMM D, YYYY')}</p>
            <p className="text-sm text-gray-500 mt-0.5">{dayjs(event.start_datetime).format('h:mm A')} — {dayjs(event.end_datetime).format('h:mm A')}</p>
          </div>
        </div>

        {/* Where */}
        <div className="mt-4">
          <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-wider mb-1.5">Where</h3>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-[15px] font-extrabold text-gray-900">{event.venue_name}</p>
            <p className="text-sm text-gray-500 mt-0.5">{event.venue_address}</p>
          </div>
        </div>

        {/* About */}
        <div className="mt-4">
          <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-wider mb-1.5">About</h3>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500 leading-relaxed">{event.description}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-col gap-2">
          {event.status === 'draft' && (
            <>
              <button data-testid="oe-submit-btn" onClick={() => doAction('submit')} disabled={busy} className="flex items-center justify-center gap-2 bg-brand-magenta hover:bg-pink-700 disabled:opacity-60 text-white py-3.5 rounded-2xl font-black text-[15px] transition-colors">
                <Rocket className="w-4 h-4" /> Publish (Go Live)
              </button>
              <button data-testid="oe-edit-btn" onClick={() => navigate(`/organizer/events/${id}/edit`)} className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-900 py-3.5 rounded-2xl font-black text-[15px] hover:bg-gray-50 transition-colors">
                <Edit className="w-4 h-4" /> Edit
              </button>
              <button data-testid="oe-delete-btn" onClick={doDelete} disabled={busy} className="flex items-center justify-center gap-2 bg-red-50 text-red-500 py-3.5 rounded-2xl font-black text-[15px] hover:bg-red-100 transition-colors">
                <Trash2 className="w-4 h-4" /> Delete Draft
              </button>
            </>
          )}
          {event.status === 'live' && (
            <>
              <button data-testid="oe-edit-btn" onClick={() => navigate(`/organizer/events/${id}/edit`)} className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-900 py-3.5 rounded-2xl font-black text-[15px] hover:bg-gray-50 transition-colors">
                <Edit className="w-4 h-4" /> Edit
              </button>
              {endPassed && (
                <button data-testid="oe-complete-btn" onClick={() => doAction('complete')} disabled={busy} className="flex items-center justify-center gap-2 bg-brand-magenta hover:bg-pink-700 disabled:opacity-60 text-white py-3.5 rounded-2xl font-black text-[15px] transition-colors">
                  <CheckCheck className="w-4 h-4" /> Mark Completed
                </button>
              )}
              <button data-testid="oe-cancel-btn" onClick={confirmCancel} disabled={busy} className="flex items-center justify-center gap-2 bg-red-50 text-red-500 py-3.5 rounded-2xl font-black text-[15px] hover:bg-red-100 transition-colors">
                <XCircle className="w-4 h-4" /> Cancel Event
              </button>
            </>
          )}
          {(event.status === 'completed' || event.status === 'cancelled') && (
            <p className="text-center text-sm text-gray-500 py-4">This event is {event.status}. No further actions available.</p>
          )}
        </div>
      </div>
    </div>
  );
}
